import type { Failure } from '../lib/refusal.js';
import { isRetryable } from '../lib/refusal.js';
import { Icons, StateCard, type StateTone } from './StateCard.js';

const MARK: Record<string, { icon: JSX.Element; tone: StateTone }> = {
  event_ended: { icon: Icons.calendar, tone: 'quiet' },
  form_closed: { icon: Icons.lock, tone: 'quiet' },
  link_revoked: { icon: Icons.lock, tone: 'alert' },
};

const BY_KIND: Record<Failure['kind'], { icon: JSX.Element; tone: StateTone }> = {
  refused: { icon: Icons.lock, tone: 'quiet' },
  not_found: { icon: Icons.compass, tone: 'quiet' },
  offline: { icon: Icons.offline, tone: 'alert' },
  error: { icon: Icons.warning, tone: 'alert' },
};

/**
 * A refusal, rendered in the event's brand.
 *
 * Every branch here is a *state*, not an error page: an expired link and a
 * closed form are ordinary things that happen to a link that lives for weeks,
 * and the attendee who hits one has done nothing wrong. The retry affordance
 * only appears where retrying could actually change the answer — offering
 * "Try again" on a revoked link would just be a second dead end.
 */
export function FailureView({ failure, onRetry }: { failure: Failure; onRetry?: () => void }) {
  const mark = (failure.reason && MARK[failure.reason]) || BY_KIND[failure.kind];
  const canRetry = Boolean(onRetry) && isRetryable(failure);

  return (
    <StateCard
      icon={mark.icon}
      title={failure.title}
      body={failure.body}
      hint={failure.hint}
      tone={mark.tone}
      live
      action={
        canRetry ? (
          <button type="button" className="e67-btn-primary" onClick={onRetry}>
            Try again
          </button>
        ) : undefined
      }
    />
  );
}
